import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router';

import { useProjectAuthorizers, useUser } from '@/providers';
import {
  getProjectDetailsPath,
  getProjectsDashboardPath,
} from '@/util/routePaths';

const AUTHORIZER_TYPE_LABELS = {
  api_key: 'API Key',
  oidc: 'OIDC',
};

function formatAuthorizerType(type) {
  return AUTHORIZER_TYPE_LABELS[type] || type || '';
}

function formatProjectAuthorizerOption(authorizer) {
  if (!authorizer) {
    return '';
  }
  const type_label = formatAuthorizerType(authorizer.type);
  if (authorizer.name && type_label) {
    return `${authorizer.name} (${type_label})`;
  }
  return authorizer.name || authorizer.id || '';
}

const PersonalAuthorizerInput = forwardRef(function PersonalAuthorizerInput(
  { className, id },
  ref,
) {
  return (
    <Box
      ref={ref}
      id={id}
      className={className}
      component='div'
      sx={{
        '&.MuiInputBase-input': {
          height: 'auto',
          whiteSpace: 'normal',
        },
      }}
    >
      <Typography variant='body1' color='text.disabled' component='div'>
        Authorizers not supported for the Personal project.{' '}
        <Link to={getProjectsDashboardPath()}>Create a new project</Link> and
        add an authorizer to enable this feature
      </Typography>
    </Box>
  );
});

function DisabledAuthorizerSelect({ label, message }) {
  return (
    <FormControl fullWidth disabled>
      <InputLabel id='endpoint-project-authorizer-label' shrink>
        {label}
      </InputLabel>
      <Select
        labelId='endpoint-project-authorizer-label'
        label={label}
        value='placeholder'
        renderValue={() => message}
        sx={{
          '& .MuiSelect-select': {
            whiteSpace: 'normal',
          },
        }}
      >
        <MenuItem value='placeholder'>{message}</MenuItem>
      </Select>
    </FormControl>
  );
}

export default function EndpointProjectAuthorizer({
  initialValues,
  onChange,
  edit = false,
}) {
  const user = useUser();
  const { authorizers, loading, project_id } = useProjectAuthorizers();
  const is_personal = Boolean(user?.id && project_id === user.id);
  const waiting_for_project = !user?.id || !project_id;
  const [selected_id, setSelectedId] = useState(initialValues?.id || '');
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (waiting_for_project) {
      return;
    }
    if (is_personal) {
      if (selected_id) {
        setSelectedId('');
      }
      onChangeRef.current(undefined, false);
      return;
    }
    if (loading || authorizers == null) {
      return;
    }
    const still_valid =
      !selected_id || authorizers.some((item) => item.id === selected_id);
    if (!still_valid) {
      setSelectedId('');
      return;
    }
    onChangeRef.current(selected_id ? { id: selected_id } : undefined, false);
  }, [
    is_personal,
    waiting_for_project,
    loading,
    authorizers,
    selected_id,
  ]);

  const authorizer_list = authorizers || [];
  const known_selection = authorizer_list.some((item) => item.id === selected_id)
    ? selected_id
    : '';

  let field = null;
  if (is_personal) {
    field = (
      <TextField
        fullWidth
        label='Authorizer'
        value='personal'
        sx={{
          '& .MuiOutlinedInput-root fieldset': {
            borderColor: 'action.disabled',
          },
          '& .MuiInputLabel-root': {
            color: 'text.disabled',
          },
        }}
        slotProps={{
          input: {
            readOnly: true,
            inputComponent: PersonalAuthorizerInput,
          },
          inputLabel: { shrink: true },
        }}
      />
    );
  } else if (waiting_for_project || loading) {
    field = (
      <DisabledAuthorizerSelect
        label='Authorizer'
        message='Loading authorizers...'
      />
    );
  } else if (authorizer_list.length === 0) {
    field = (
      <>
        <DisabledAuthorizerSelect
          label='Authorizer'
          message='No Authorizers Configured for this Project'
        />
        <FormHelperText>
          Configure authorizers on the{' '}
          <Link to={getProjectDetailsPath(project_id)}>
            project configuration page
          </Link>
          .
        </FormHelperText>
      </>
    );
  } else {
    field = (
      <FormControl fullWidth disabled={edit}>
        <InputLabel id='endpoint-project-authorizer-label' shrink>
          Authorizer
        </InputLabel>
        <Select
          labelId='endpoint-project-authorizer-label'
          label='Authorizer'
          displayEmpty
          value={known_selection}
          onChange={(event) => setSelectedId(event.target.value)}
          renderValue={(value) => {
            if (!value) {
              return 'None';
            }
            const match = authorizer_list.find((item) => item.id === value);
            return formatProjectAuthorizerOption(match) || value;
          }}
        >
          <MenuItem value=''>None</MenuItem>
          {authorizer_list.map((authorizer) => (
            <MenuItem key={authorizer.id} value={authorizer.id}>
              {formatProjectAuthorizerOption(authorizer)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
          Authorization
        </Typography>
      </Grid>
      <Grid size={12}>{field}</Grid>
    </Grid>
  );
}
